<script setup>
import { ref, computed, watch } from "vue"
import Dialog from "primevue/dialog"
import Dropdown from "primevue/dropdown"
import Button from "primevue/button"
import InputText from "primevue/inputtext"

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  carts: {
    type: Array,
    default: () => [],
  },
  product: {
    type: Object,
    default: null,
  },
  variantSummary: {
    type: Object,
    default: null,
  },
  selectedVariant: {
    type: Object,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(["close", "confirm"])

const selectedCartId = ref(null)
const newCartName = ref("")
const selectedRoomId = ref(null)
const isCreatingNewRoom = ref(false)
const newRoomName = ref("")
const showCartError = ref(false)
const roomError = ref("")

const selectedCart = computed(() => {
  return props.carts.find((c) => c.id === selectedCartId.value)
})

const productVariant = computed(() => {
  return props.selectedVariant || props.variantSummary?.defaultVariant || null
})

const displayedVariantPrice = computed(() => {
  if (productVariant.value?.price == null) {
    return null
  }
  return formatCurrencyPrice(productVariant.value.price, productVariant.value.currency)
})

const visibleVariantFromLabel = computed(() => {
  return !props.selectedVariant && props.variantSummary?.priceIsFrom
})

const quantity = ref(1)

const roomsWithUncategorized = computed(() => {
  const rooms = selectedCart.value?.rooms || []

  const mappedRooms = rooms.map((room) => ({
    id: room.id,
    name: room.name,
    icon: "pi pi-building",
    itemsCount: room.items?.length || 0,
  }))

  return [
    {
      id: null,
      name: "📁 Без раздела",
      icon: "pi pi-inbox",
      itemsCount: selectedCart.value?.cartItems?.length || 0,
    },
    ...mappedRooms,
  ]
})

const hasRooms = computed(() => {
  return (selectedCart.value?.rooms?.length || 0) > 0
})

const canAdd = computed(() => {
  const hasValidCart = selectedCartId.value !== null || newCartName.value.trim()
  return hasValidCart && !props.loading
})

const dialogTitle = computed(() => {
  return props.product ? `Добавить "${props.product.name}"` : "Добавить товар"
})

const productName = computed(() => props.product?.name || "")
const productBrand = computed(() => props.product?.brandName || "")
const productImage = computed(() => props.product?.defaultImageUrl || "/placeholder.png")

const productPrice = computed(() => {
  if (!props.product) return 0

  const salePriceKzt = Number(props.product.salePriceKzt)
  const basePriceKzt = Number(props.product.basePriceKzt)
  const priceKzt = Number(props.product.priceKzt ?? props.product.PriceKzt)

  if (
    props.product.saleIsActive &&
    props.product.salePriceKzt !== null &&
    props.product.salePriceKzt !== undefined &&
    props.product.salePriceKzt !== "" &&
    Number.isFinite(salePriceKzt)
  ) {
    return salePriceKzt
  }

  if (Number.isFinite(basePriceKzt)) return basePriceKzt
  if (Number.isFinite(priceKzt)) return priceKzt

  return 0
})

function formatPrice(price) {
  const numericPrice = Number(price || 0)

  return new Intl.NumberFormat("ru-KZ", {
    maximumFractionDigits: 0,
  }).format(Number.isFinite(numericPrice) ? Math.round(numericPrice) : 0) + " ₸"
}

function formatCurrencyPrice(price, currency) {
  const numericPrice = Number(price || 0)
  const formattedNumber = new Intl.NumberFormat("ru-RU", {
    maximumFractionDigits: 0,
  }).format(Number.isFinite(numericPrice) ? Math.round(numericPrice) : 0)

  return currency ? `${formattedNumber} ${currency}` : `${formattedNumber} ₸`
}

function getCartItemsCount(cart) {
  const roomsItems = (cart.rooms || []).reduce(
    (sum, r) => sum + (r.items?.length || 0),
    0
  )
  const plainItems = (cart.cartItems || []).length
  return roomsItems + plainItems
}

function clearErrors() {
  showCartError.value = false
  roomError.value = ""
}

function resetState() {
  selectedCartId.value = null
  newCartName.value = ""
  selectedRoomId.value = null
  isCreatingNewRoom.value = false
  newRoomName.value = ""
  quantity.value = 1
  clearErrors()
}

function handleClose() {
  if (props.loading) return
  resetState()
  emit("close")
}

function toggleNewRoomInput() {
  clearErrors()
  isCreatingNewRoom.value = !isCreatingNewRoom.value

  if (!isCreatingNewRoom.value) {
    newRoomName.value = ""
  } else {
    selectedRoomId.value = null
  }
}

function cancelNewRoom() {
  isCreatingNewRoom.value = false
  newRoomName.value = ""
  roomError.value = ""
}

function decrementQuantity() {
  quantity.value = Math.max(1, quantity.value - 1)
}

function incrementQuantity() {
  quantity.value += 1
}

function createNewRoom() {
  roomError.value = ""

  if (!newRoomName.value.trim() || !selectedCart.value) {
    roomError.value = "Введите название нового раздела"
    return
  }

  emit("confirm", {
    cartId: selectedCartId.value,
    roomId: null,
    createNewRoom: true,
    newRoomName: newRoomName.value.trim(),
    product: props.product,
    quantity: quantity.value,
  })

  handleClose()
}

function createAndAdd() {
  showCartError.value = false

  if (!newCartName.value.trim()) {
    showCartError.value = true
    return
  }

  emit("confirm", {
    cartId: null,
    createNewCart: true,
    newCartName: newCartName.value.trim(),
    roomId: selectedRoomId.value === null ? null : selectedRoomId.value,
    createNewRoom: false,
    product: props.product,
    quantity: quantity.value,
  })

  handleClose()
}

function confirm() {
  if (props.loading) return

  clearErrors()

  if (selectedCartId.value !== null) {
    emit("confirm", {
      cartId: selectedCartId.value,
      createNewCart: false,
      roomId: selectedRoomId.value === null ? null : selectedRoomId.value,
      createNewRoom: false,
      product: props.product,
      quantity: quantity.value,
    })
    handleClose()
    return
  }

  if (newCartName.value.trim()) {
    createAndAdd()
    return
  }

  showCartError.value = true
}

watch(
  () => props.visible,
  (newVal) => {
    if (!newVal) {
      resetState()
    } else {
      resetState()
    }
  }
)

watch(selectedCartId, () => {
  selectedRoomId.value = null
  isCreatingNewRoom.value = false
  newRoomName.value = ""
  roomError.value = ""
  showCartError.value = false
})
</script>

<template>
  <Dialog
    :visible="props.visible"
    modal
    :header="dialogTitle"
    :style="{ width: '480px' }"
    class="add-to-cart-modal"
    :closable="!props.loading"
    @update:visible="handleClose"
  >
    <div class="modal-content">
      <div class="step">
        <div class="step-header">
          <span class="step-number">1</span>
          <span class="step-title">Выберите проект</span>
        </div>

        <div class="step-body">
          <Dropdown
            v-model="selectedCartId"
            :options="props.carts"
            optionLabel="name"
            optionValue="id"
            placeholder="Выберите проект"
            :class="{ 'p-invalid': showCartError }"
            class="w-full"
          >
            <template #option="slotProps">
              <div class="cart-option">
                <i class="pi pi-folder" />
                <span>{{ slotProps.option.name }}</span>
                <span class="cart-items-count">
                  {{ getCartItemsCount(slotProps.option) }} поз.
                </span>
              </div>
            </template>
          </Dropdown>

          <small v-if="showCartError" class="field-error">
            Выберите проект или введите название нового
          </small>

          <div class="divider">
            <span>или</span>
          </div>

          <div class="new-cart-input">
            <InputText
              v-model="newCartName"
              placeholder="Название нового проекта"
              @keyup.enter="createAndAdd"
            />
            <Button
              label="Создать"
              icon="pi pi-plus"
              severity="secondary"
              outlined
              :disabled="props.loading"
              @click="createAndAdd"
            />
          </div>
        </div>
      </div>

      <div v-if="selectedCartId !== null && selectedCart" class="step">
        <div class="step-header">
          <span class="step-number">2</span>
          <span class="step-title">Выберите раздел</span>
        </div>

        <div class="step-body">
          <div class="rooms-list">
            <div
              v-for="room in roomsWithUncategorized"
              :key="room.id"
              class="room-option"
              :class="{ active: selectedRoomId === room.id }"
              @click="selectedRoomId = room.id"
            >
              <i :class="room.icon" />
              <span class="room-name">{{ room.name }}</span>
              <span class="room-items-count">{{ room.itemsCount }} поз.</span>
              <i v-if="selectedRoomId === room.id" class="pi pi-check check-icon" />
            </div>

            <div
              class="room-option new-room"
              :class="{ active: isCreatingNewRoom }"
              @click="toggleNewRoomInput"
            >
              <i class="pi pi-plus-circle" />
              <span class="room-name">Новый раздел</span>
            </div>

            <div v-if="isCreatingNewRoom" class="new-room-input">
              <InputText
                v-model="newRoomName"
                placeholder="Название раздела"
                autofocus
                @keyup.enter="createNewRoom"
              />
              <small v-if="roomError" class="field-error">
                {{ roomError }}
              </small>
              <div class="new-room-actions">
                <Button
                  label="Создать"
                  size="small"
                  severity="primary"
                  :disabled="props.loading"
                  @click="createNewRoom"
                />
                <Button
                  label="Отмена"
                  size="small"
                  text
                  :disabled="props.loading"
                  @click="cancelNewRoom"
                />
              </div>
            </div>
          </div>

          <small v-if="!hasRooms && !isCreatingNewRoom" class="hint">
            <i class="pi pi-info-circle" />
            Нет разделов. Создайте новый раздел или оставьте "Без раздела"
          </small>
        </div>
      </div>

      <div v-if="props.product" class="product-info">
        <img :src="productImage" :alt="productName" class="product-image" />
        <div class="product-details">
          <div class="product-brand">{{ productBrand }}</div>
          <div class="product-name">{{ productName }}</div>
          <div class="product-price">
            {{ displayedVariantPrice ?? formatPrice(productPrice) }}
          </div>
          <div v-if="visibleVariantFromLabel" class="price-from">от</div>
          <div class="quantity-selector">
            <span class="quantity-label">Кол-во</span>
            <div class="quantity-controls">
              <Button type="button" icon="pi pi-minus" severity="secondary" text :disabled="quantity <= 1 || props.loading" @click="decrementQuantity" />
              <span class="quantity-value">{{ quantity }}</span>
              <Button type="button" icon="pi pi-plus" severity="secondary" text :disabled="props.loading" @click="incrementQuantity" />
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <Button
          label="Отмена"
          text
          :disabled="props.loading"
          @click="handleClose"
        />
        <Button
          label="Добавить"
          severity="primary" 
          :disabled="!canAdd"
          :loading="props.loading"
          @click="confirm"
        />
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
.add-to-cart-modal :deep(.p-dialog-header) {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 20px 24px;
}

.add-to-cart-modal :deep(.p-dialog-header-icon) {
  color: #64748b;
}

.add-to-cart-modal :deep(.p-dialog-content) {
  padding: 0;
}

.modal-content {
  padding: 24px;
}

.step {
  margin-bottom: 28px;
}

.step:last-of-type {
  margin-bottom: 20px;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.step-number {
  width: 28px;
  height: 28px;
  background: #c41217;
  color: white;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
}

.step-title {
  font-weight: 600;
  color: #1e293b;
  font-size: 15px;
}

.step-body {
  padding-left: 40px;
}

.w-full {
  width: 100%;
}

.cart-option {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cart-option i {
  color: #c41217;
  font-size: 14px;
}

.cart-items-count {
  margin-left: auto;
  font-size: 12px;
  color: #94a3b8;
}

.divider {
  text-align: center;
  margin: 16px 0;
  position: relative;
}

.divider span {
  background: white;
  padding: 0 12px;
  color: #94a3b8;
  font-size: 12px;
}

.divider::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: #e2e8f0;
  z-index: 0;
}

.divider span {
  position: relative;
  z-index: 1;
}

.new-cart-input {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: center;
}

.rooms-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.room-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
}

.room-option:hover {
  border-color: #c41217;
  background: #fff5f5;
}

.room-option.active {
  border-color: #c41217;
  background: #fff5f5;
}

.room-option i {
  color: #64748b;
  font-size: 16px;
}

.room-option.active i {
  color: #c41217;
}

.room-name {
  flex: 1;
  font-weight: 500;
  font-size: 14px;
}

.room-items-count {
  font-size: 12px;
  color: #94a3b8;
}

.check-icon {
  color: #22c55e !important;
  font-size: 16px;
}

.room-option.new-room {
  border-style: dashed;
  background: #f8fafc;
}

.room-option.new-room i {
  color: #c41217;
}

.new-room-input {
  margin-top: 12px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 10px;
}

.new-room-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.hint {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 12px;
  margin-top: 12px;
}

.product-info {
  display: flex;
  gap: 16px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
  margin-bottom: 24px;
}

.product-image {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  object-fit: cover;
  background: #f1f5f9;
}

.product-details {
  flex: 1;
}

.product-brand {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.product-name {
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 6px;
}

.product-price {
  font-weight: 700;
  color: #c41217;
  font-size: 16px;
}

.price-from {
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
  text-transform: uppercase;
}

.quantity-selector {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
}

.quantity-label {
  font-size: 13px;
  color: #64748b;
}

.quantity-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
}

.quantity-value {
  min-width: 28px;
  text-align: center;
  font-weight: 600;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
  margin-top: 8px;
}

.field-error {
  color: #dc2626;
  font-size: 12px;
  margin-top: 6px;
}

:deep(.p-dropdown.p-invalid) {
  border-color: #ef4444;
}

@media (max-width: 550px) {
  .add-to-cart-modal :deep(.p-dialog) {
    width: calc(100vw - 32px) !important;
    margin: 16px;
  }

  .modal-content {
    padding: 20px;
  }

  .step-body {
    padding-left: 0;
  }

  .step-header {
    margin-bottom: 12px;
  }

  .new-cart-input {
    grid-template-columns: 1fr;
  }
}
</style>
